import { shippingRequestHttp } from "@/api";
import { BaseService } from "@/api/services/baseService";
import type {
    GeoPoint,
    ShipDto,
    ShipHistoryForm,
    ShipInfoVo,
    ShipSmartVo,
    ShipVo,
    TransportOrderShip
} from "@zhihaoscm/entity";
import type { Page } from "@zhihaoscm/entity";
import type { Ship } from "@zhihaoscm/entity";
import { confirmable } from "@/utils/decorator/confirmable";
import type { Device } from "@zhihaoscm/entity";
import type {
    AisTrackForm,
    BindParams,
    ShipForm,
    ShipInspectionReportVo,
    FindSimpleProfileByIdRes,
    FindProfileByIdRes,
    FindPortReportPageRes,
    ShipUpdateForm
} from "@/entity/logistics/ship";
import type { TransportListPageParams } from "@/views/pages/logistics/findLocation/type";
export class ShipService extends BaseService<any, any, any, any> {
    // 分页查询
    public async paging(params: any): Promise<Page<ShipVo[]>> {
        const { data } = await this.service.get(`${this.path}/paging`, { params });
        return data;
    }
    public async getInfo(id: number | string): Promise<ShipVo> {
        const { data } = await this.service.get(`${this.path}/vo/${id}`);
        return data;
    }
    @confirmable("是否提交")
    public async updateData(id: string | number, params: Partial<ShipUpdateForm>): Promise<void> {
        await this.service.put(`${this.path}/${id}`, params);
    }
    @confirmable("是否提交")
    public async create(params: Partial<ShipForm>): Promise<void> {
        await this.service.post(`${this.path}`, params);
    }
    public async findDevice(id: string | number): Promise<Device[]> {
        const { data } = await this.service.get(`${this.path}/find/devices/${id}`);
        return data;
    }
    public async extent(form: ShipDto, showScope: boolean = false): Promise<ShipInfoVo[]> {
        const { data } = await this.service.post(
            `${this.path}/extent${showScope ? "/scope" : ""}`,
            {
                ...form
            },
            {
                timeout: 60000
            }
        );
        return data;
    }
    public async search(keyword: string, limit: number = 10, type: string = "SHIP"): Promise<Ship[]> {
        const { data } = await this.service.get(`${this.path}/keyword`, { params: { keyword, limit, type } });
        return data;
    }
    /**关键字查询*/
    public async searchVo(keyword: string): Promise<ShipVo[]> {
        const { data } = await this.service.get(`${this.path}/vo/keyword`, { params: { keyword } });
        return data;
    }
    public async findCreate(id: string): Promise<ShipInfoVo> {
        const { data } = await this.service.get(`${this.path}/find-create/${id}`);
        return data;
    }
    public async aisTrack(form: AisTrackForm): Promise<any[]> {
        const { data } = await this.service.request({
            method: "get",
            url: `${this.path}/aisTrack`,
            params: {
                mmsi: form.mmsi,
                start: new Date(form.start).getTime(),
                end: new Date(form.end).getTime()
            }
        });
        return data;
    }
    @confirmable("是否提交，提交后此船舶将绑定认证此承运商")
    public async bind(id: string | number, params: BindParams): Promise<void> {
        await this.service.put(`${this.path}/bind/${id}`, params);
    }
    @confirmable("是否解绑，确认后此船舶将解绑认证此承运商")
    public async unbind(id: string | number): Promise<void> {
        await this.service.put(`${this.path}/unbind/${id}`);
    }
    // 查询并校验船舶
    public async findByIdAndValidate(id: number | string): Promise<ShipVo> {
        const { data } = await this.service.get(`${this.path}/find-validate/${id}`);
        return data;
    }
    // 查询并创建船舶
    public async findByIdAndCreate(id: number | string) {
        const { data } = await this.service.get(`${this.path}/find-create/${id}`);
        return data;
    }
    // 船舶下拉列表
    public async getSelector(params: any): Promise<Page<ShipVo>> {
        const { data } = await this.service.get(`${this.path}/selector-vo`, { params });
        return data;
    }
    public async getShipSelector(params: any): Promise<Page<Ship>> {
        const { data } = await this.service.get(`${this.path}/selector`, { params });
        return data;
    }

    public async getHistoryList(): Promise<Ship[]> {
        const { data } = await this.service.get(`${this.path}/history`);
        return data;
    }

    @confirmable("是否清空")
    public async clearHistory(): Promise<void> {
        const { data } = await this.service.delete(`${this.path}/all-history`);
        return data;
    }
    public async deleteHistory(form: ShipHistoryForm): Promise<void> {
        const { data } = await this.service.delete(`${this.path}/history`, { data: form });
        return data;
    }

    public async addHistory(form: ShipHistoryForm): Promise<void> {
        const { data } = await this.service.post(`${this.path}/history`, form);
        return data;
    }

    // 安装推广船舶下拉列表
    public async installSelector(params: any): Promise<Page<ShipVo>> {
        const { data } = await this.service.get(`${this.path}/install/selector`, { params });
        return data;
    }

    /**
     * 识别内河船舶检验报告
     * @param fileIds
     */
    public async recognizeShipInspectionReport(fileIds: number[]): Promise<ShipInspectionReportVo> {
        const { data } = await this.service.get(`${this.path}/recognize-ship-inspection-report`, {
            params: { fileIds }
        });
        return data;
    }

    /**
     * 自定义艘船
     */
    public async getShipCustom(params: { geoPointList: GeoPoint[]; time?: number }): Promise<ShipInfoVo[]> {
        const { data } = await this.service.post(`${this.path}/extent/custom`, params, {
            timeout: 60000
        });
        return data;
    }

    /**
     * 查询已报价船舶
     * @param id 货主找船需求id
     */
    public async getShipAccept(id: number): Promise<ShipSmartVo[]> {
        const { data } = await this.service.get(`${this.path}/find/accept/${id}`);
        return data;
    }

    /**
     * 查船-运单信息
     */
    public async getTransportListByShipId(params: TransportListPageParams): Promise<Page<TransportOrderShip>> {
        const { data } = await this.service.get(`${this.path}/transport`, { params });
        return data;
    }

    /**
     * 查询承运商已认证船舶列表
     * */
    public async carrierShip(params: { customerId: number; keyword: string }): Promise<Ship[]> {
        const { data } = await this.service.get(`${this.path}/carrier`, { params });
        return data;
    }

    // 船舶营业运输证提取
    public async getShipBusinessTransport(fileId: number) {
        const { data } = await this.service.post(`${this.path}/get-ship-business-transport?fileId=${fileId}`);
        return data;
    }

    // 识别船舶船员适任证书
    public async recognizeCrewCertificate(fileId: number): Promise<string> {
        const { data } = await this.service.post(`${this.path}/recognize-crew-certificate?fileId=${fileId}`);
        return data;
    }

    // 查询船舶信息（船舶所在位置）
    public async getShipLocation(params: { mmsi: string }): Promise<ShipInfoVo> {
        const { data } = await this.service.get(`${this.path}/location`, { params });
        return data;
    }

    // 查询船舶对接认证列表
    public async getCapacityCertificationList(params: any): Promise<any> {
        const { data } = await this.service.get(`${this.path}/auth/paging`, { params });
        return data;
    }
    // 运力认证签好运刷新按钮
    public async refreshCapacityCertificationQianhaoyun(id: string): Promise<any> {
        const { data } = await this.service.get(`${this.path}/refresh/${id}`);
        return data;
    }
    // 运力认证签好运重新认证按钮
    public async reAuthCapacityCertificationQianhaoyun(id: string): Promise<any> {
        const { data } = await this.service.put(`${this.path}/auth/${id}`);
        return data;
    }
    // 运力认证黄马港刷新按钮
    public async refreshCapacityCertificationHuangmagang(id: string): Promise<any> {
        const { data } = await this.service.get(`${this.path}/hmg-refresh/${id}`);
        return data;
    }
    // 运力认证黄马港重新认证按钮
    public async reAuthCapacityCertificationHuangmagang(id: string): Promise<any> {
        const { data } = await this.service.put(`${this.path}/hmg-check/${id}`);
        return data;
    }
    // 查询船舶简单信息
    public async getShipSimpleProfile(id: number): Promise<FindSimpleProfileByIdRes> {
        const { data } = await this.service.get(`${this.path}/simple/profile/${id}`);
        return data;
    }

    /**
     * 船舶画像
     * @param id 船舶ID
     * @param scope 时间范围 1: 日 2: 周  3:月 4: 季度  5: 半年 6:年（与日期范围二选一）
     * @param startDate 开始时间（与scope二选一）
     * @param endDate 结束时间（与scope二选一）
     * @returns
     */
    public async findProfileById(
        id: string,
        scope?: number,
        startDate?: string | Date,
        endDate?: string | Date
    ): Promise<FindProfileByIdRes> {
        const params: Record<string, any> = {};
        
        // 只传递存在的参数，scope和日期范围不能同时存在
        if (scope !== undefined && scope !== 0) {
            params.scope = scope;
        }
        
        if (startDate && endDate) {
            // 格式化日期，只精确到分钟
            const formatDate = (date: string | Date): string => {
                if (typeof date === "string") {
                    // 如果已经是字符串，确保格式正确（只到分钟）
                    const parts = date.split("T");
                    const dateStr = parts[0] || "";
                    const timePart = parts[1] ? parts[1].substring(0, 5) : "00:00";
                    return `${dateStr}T${timePart}`;
                } else {
                    // Date 对象转换为 YYYY-MM-DDTHH:mm 格式
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const day = String(date.getDate()).padStart(2, "0");
                    const hours = String(date.getHours()).padStart(2, "0");
                    const minutes = String(date.getMinutes()).padStart(2, "0");
                    return `${year}-${month}-${day}T${hours}:${minutes}`;
                }
            };
            params.startDate = formatDate(startDate);
            params.endDate = formatDate(endDate);
        }
        
        const { data } = await this.service.get(`/ship/profile/${id}`, {
            params
        });
        return data;
    }

    /**
     * 进出港记录列表
     * @param id 船舶id
     * @param page 页码
     * @param size 每页条数
     * @param scope 时间范围 1: 日 2: 周  3:月 4: 季度  5: 半年 6:年（与日期范围二选一）
     * @param startDate 开始时间（与scope二选一）
     * @param endDate 结束时间（与scope二选一）
     * @returns
     */
    public async findPortReportPage(
        id: string,
        page: number,
        size: number,
        scope: number,
        startDate: string | Date,
        endDate: string | Date
    ): Promise<FindPortReportPageRes> {
        const params: Record<string, any> = {
            id,
            page,
            size
        };
        
        if (startDate && endDate && startDate !== "" && endDate !== "") {
            params.startDate = typeof startDate === "string" ? startDate : startDate.toISOString();
            params.endDate = typeof endDate === "string" ? endDate : endDate.toISOString();
        } else if (scope !== undefined && scope !== null && scope !== 0) {
        
            params.scope = scope;
        }
        
        const { data } = await this.service.get(`/ship/port/report/paging`, {
            params
        });
        return data;
    }
}
export const shipService = new ShipService("/ship", shippingRequestHttp.service);
